// invoiceParser.js — Парсинг PDF-инвойсов (Казахстан: Счета на оплату, Платежные поручения)
// Извлекает: IBAN, БИН, название, сумму, описание
//
// Stage 11a: обновлено на pdfjs-dist@4.x (закрытие CVE GHSA-wgrm-67xf-hhpq —
// arbitrary JavaScript execution upon opening malicious PDF, fix in v4.2+).
// 4.x — ESM-only, импорт через 'pdfjs-dist' (без /build/pdf), worker — .mjs.

import * as pdfjsLib from 'pdfjs-dist';

// Worker из public/ — работает в локальной сети без CDN
pdfjsLib.GlobalWorkerOptions.workerSrc = process.env.PUBLIC_URL + '/pdf.worker.min.mjs';

// Извлечь текст из PDF-файла (File object)
async function extractText(file) {
  const buf = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
  let text = '';
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    text += content.items.map(item => item.str).join(' ') + '\n';
  }
  return text;
}

// Очистка IBAN от суффиксов валют (KZT, USD, EUR и т.д.)
function cleanIBAN(raw) {
  if (!raw) return raw;
  let iban = raw.replace(/\s+/g, '').toUpperCase();
  // Стандартный IBAN Казахстана: KZ + 18 символов = 20 символов
  // Если длиннее и заканчивается на 3 буквы (код валюты) — обрезаем
  if (iban.length > 20 && /[A-Z]{3}$/.test(iban)) {
    iban = iban.slice(0, -3);
  }
  return iban;
}

// Найти все IBAN (казахстанский формат KZ + 18 символов)
// Убираем суффиксы валют (KZT, USD, EUR и т.д.) которые иногда идут через пробел
function findIBANs(text) {
  const matches = text.match(/KZ\d{18}/g) || [];
  // Очищаем: если IBAN заканчивается на код валюты (3 буквы), убираем
  return [...new Set(matches.map(iban => {
    // KZ + 18 цифр/букв = 20 символов — стандартный IBAN Казахстана
    // Если длина > 20 и последние 3 символа — буквы (валюта), обрезаем
    if (iban.length > 20 && /[A-Za-z]{3}$/.test(iban)) {
      return iban.slice(0, -3);
    }
    return iban;
  }))];
}

// Найти все БИН/ИИН (12 цифр)
function findBINs(text) {
  // Ищем 12-значные числа рядом с ключевыми словами
  const contextMatches = [];
  const patterns = [
    /(?:БИН|ИИН|БИН\s*\/\s*ИИН|ИИН\s*\(БИН\)|ИИН\(БИН\))[:\s]*(\d{12})/gi,
  ];
  for (const p of patterns) {
    let m;
    while ((m = p.exec(text)) !== null) {
      contextMatches.push(m[1]);
    }
  }
  return [...new Set(contextMatches)];
}

// Найти сумму
function findAmount(text) {
  // Приоритет (сверху вниз = выше приоритет):
  // «Всего к оплате» — финальная сумма с НДС, должно идти ПЕРЕД «Итого» (которое
  // часто = «без НДС» и меньше реальной). Task #47: добавили необязательные «, тенге»
  // / «, тг» / «₸» / «KZT» между словами и числом — Yandex format «Всего к оплате, тенге 250 000,00».
  const patterns = [
    /Всего к оплате[\s,]*(?:тенге|тг|₸|KZT)?\s*([0-9\s]+[.,]\d{2})/i,
    /К оплате[\s:]*([0-9\s]+[.,]\d{2})/i,
    /Итого к оплате[\s,]*(?:тенге|тг|₸|KZT)?\s*([0-9\s]+[.,]\d{2})/i,
    /Итого[:\s]*([0-9\s]+[.,]\d{2})/i,
    /на сумму\s+(\d[\d\s]*\d)\s*(?:KZT|тенге|₸)/i,
    /на сумму\s+([0-9\s]+[.,]?\d*)/i,
    /Сумма[:\s]*([0-9\s]+[.,]\d{2})/i,
  ];

  for (const p of patterns) {
    const m = text.match(p);
    if (m) {
      const numStr = m[1].replace(/\s/g, '').replace(',', '.');
      const val = parseFloat(numStr);
      if (val > 0) return val;
    }
  }

  // Для платежных поручений — крупная сумма в начале
  const bigNum = text.match(/(?:плательщик|Фактический)[^]*?(\d[\d\s]{2,})\b/i);
  if (bigNum) {
    const val = parseFloat(bigNum[1].replace(/\s/g, ''));
    if (val >= 100) return val;
  }

  return 0;
}

// (2026-06-09) Имя контрагента бывает мусорным («Поставщик (Исполнитель): БИН 0808…»)
// из-за порядка текста pdfjs. Эти хелперы достают чистое «ТОО/ИП "…"» рядом с БИН.
function looksJunkName(s) {
  if (!s) return true;
  if (/^\s*(?:ТОО|ИП|АО|ОсОО|ООО|ЗАО)\s*["«]/i.test(s)) return false;
  return /БИН|ИИН|Покупатель|Поставщик|Заказчик|Исполнитель|Плательщик|Получатель|[:/]|\d{6}/i.test(s);
}
function findNameNearBin(text, bin) {
  if (!bin) return '';
  const idx = text.indexOf(bin);
  if (idx < 0) return '';
  const win = text.slice(Math.max(0, idx - 110), idx + 170);
  const m = win.match(/(?:ТОО|ИП|АО|ОсОО|ООО|ЗАО)\s*["«][^"«»\n]{2,70}["»]/i)
        || win.match(/(?:ТОО|ИП|АО|ОсОО|ООО|ЗАО)\s+[А-ЯЁA-Z][^,\n]{2,60}/);
  return m ? m[0].replace(/\s+/g, ' ').trim() : '';
}

// Определить тип документа и извлечь стороны
function parseParties(text, ibans, bins) {
  const result = {
    supplier: { name: '', bin: '', iban: '' },
    buyer: { name: '', bin: '', iban: '' },
    docType: 'unknown', // 'invoice' | 'payment_order'
    docNumber: '',
    docDate: '',
    docDueDate: '',   // (2026-06-09) срок оплаты, если есть в документе
    description: '',
  };

  // Определяем тип документа (Счёт/Счет — с ё и без).
  // Task #47 (2026-05-21): расширили — «Счёт №» (без «на оплату») тоже invoice.
  // Корп. счета (Yandex, и т.п.) часто имеют формат «Счёт № LST-...» без слова «на оплату».
  if (/Сч[её]т (?:на оплату|-фактура)?\s*[№#]/i.test(text) || /Сч[её]т\s+на\s+оплату/i.test(text)) {
    result.docType = 'invoice';
  } else if (/Платежное поручение/i.test(text) || /платежн\S*\s+поручени\S*/i.test(text)) {
    result.docType = 'payment_order';
  }

  // Номер документа. Task #47: расширенный regex — покрывает и «Счёт на оплату №»,
  // и просто «Счёт № LST-5673908681-1» (Yandex format).
  const numMatch = text.match(/(?:Сч[её]т (?:на оплату|-фактура)?|Платежное поручение)\s*[№#]\s*(\S+)/i);
  if (numMatch) result.docNumber = numMatch[1];
  // pdfjs-порядок: «Номер документа 7928187» (значение сразу после метки).
  if (!result.docNumber) {
    const numAlt = text.match(/Номер документа[:\s№#]*((?=\S*\d)\S+)/i);
    if (numAlt) result.docNumber = numAlt[1];
  }

  // Дата документа (формат: "02/04/2026" или "2 апреля 2026")
  const dateMatch1 = text.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  const dateMatch2 = text.match(/(\d{1,2})\s+(января|февраля|марта|апреля|мая|июня|июля|августа|сентября|октября|ноября|декабря)\s+(\d{4})/i);
  if (dateMatch1) {
    result.docDate = `${dateMatch1[3]}-${dateMatch1[2].padStart(2,'0')}-${dateMatch1[1].padStart(2,'0')}`;
  } else if (dateMatch2) {
    const months = { 'января':1,'февраля':2,'марта':3,'апреля':4,'мая':5,'июня':6,'июля':7,'августа':8,'сентября':9,'октября':10,'ноября':11,'декабря':12 };
    const mon = months[dateMatch2[2].toLowerCase()] || 1;
    result.docDate = `${dateMatch2[3]}-${String(mon).padStart(2,'0')}-${dateMatch2[1].padStart(2,'0')}`;
  }

  // (2026-06-09) Формат ps.kz и подобные: метки и значения разнесены по строкам —
  //   «Номер документа Дата составления Срок оплаты 7928187 28.05.2026 27.06.2026».
  const tripleMatch = text.match(/Номер документа\s+Дата\s+состав\S*\s+Срок\s+оплаты\s+(\S+)\s+(\d{1,2})[.\/](\d{1,2})[.\/](\d{4})\s+(\d{1,2})[.\/](\d{1,2})[.\/](\d{4})/i);
  if (tripleMatch) {
    if (!result.docNumber) result.docNumber = tripleMatch[1];
    if (!result.docDate) result.docDate = `${tripleMatch[4]}-${tripleMatch[3].padStart(2,'0')}-${tripleMatch[2].padStart(2,'0')}`;
    result.docDueDate = `${tripleMatch[7]}-${tripleMatch[6].padStart(2,'0')}-${tripleMatch[5].padStart(2,'0')}`;
  }
  // Срок оплаты отдельной меткой (dd.mm.yyyy), если не из triple.
  if (!result.docDueDate) {
    const dueM = text.match(/Срок\s+оплаты[:\s]*(\d{1,2})[.\/](\d{1,2})[.\/](\d{4})/i);
    if (dueM) result.docDueDate = `${dueM[3]}-${dueM[2].padStart(2,'0')}-${dueM[1].padStart(2,'0')}`;
  }
  // Дата составления с точками (dd.mm.yyyy), если не нашли слэш/словесный формат.
  if (!result.docDate) {
    const dDots = text.match(/Дата\s+составлени\S*[:\s]*(\d{1,2})\.(\d{1,2})\.(\d{4})/i) || text.match(/(\d{1,2})\.(\d{1,2})\.(\d{4})/);
    if (dDots) result.docDate = `${dDots[3]}-${dDots[2].padStart(2,'0')}-${dDots[1].padStart(2,'0')}`;
  }

  if (result.docType === 'invoice') {
    // === Поставщик / Получатель / Исполнитель ===
    // Task #47: расширили синонимы. Yandex/корп счета используют «Получатель»
    // вместо «Поставщик». Также бывает «Исполнитель» (договоры услуг).
    // Формат «Получатель БИН: 160540014930 ТОО «Y. Taxi Qazaqstan»» — БИН на отдельной строке.
    const supplierMatch =
      // Standard: "Поставщик: БИН/ИИН 880520351529, ИП «Лаб»"
         text.match(/Поставщик[:\s]+(?:БИН\s*\/?\s*ИИН\s+)?(\d{12})\s*[,./]?\s*([^,\n]+)/i)
      || text.match(/Поставщик[:\s]+([^,\n]+)/i)
      // Yandex/корп format: "Получатель БИН: 160540014930 ТОО «Y. Taxi Qazaqstan»"
      || text.match(/(?:Получатель|Исполнитель)[^\d]{0,80}БИН[:\s]*(\d{12})[\s\S]{0,300}?((?:ТОО|ИП|АО|ОсОО|ООО|ЗАО)\s+[«"][^«"»\n]+[»"])/i)
      || text.match(/(?:Получатель|Исполнитель)[:\s]+([^\n]+)/i);
    if (supplierMatch) {
      if (supplierMatch[2]) {
        // Формат с БИН
        result.supplier.bin = supplierMatch[1];
        result.supplier.name = supplierMatch[2].trim().replace(/^["«]|["»]$/g, '');
      } else {
        // Формат без БИН — имя поставщика
        result.supplier.name = supplierMatch[1].trim().replace(/^["«]|["»]$/g, '');
      }
    }

    // === Покупатель / Заказчик / Плательщик ===
    // Task #47: расширили синонимы. Yandex/корп счета используют «Заказчик».
    // Формат «Заказчик: ИП ЕлемесовЛаб ... БИН: 880520351529» — БИН отдельно.
    const buyerMatch =
      // Standard: "Покупатель: ИНН/БИН 210940013502 ТОО «...»"
         text.match(/Покупатель[:\s]+(?:ИНН\s*\/?\s*БИН|БИН\s*\/?\s*ИИН)\s*(\d{12})\s*[,./]*\s*([^,\n]+)/i)
      || text.match(/Покупатель[:\s]+(?:БИН\s*\/?\s*ИИН\s+)?(\d{12})\s*[,.]?\s*([^,\n]+)/i)
      || text.match(/Покупатель[:\s]+([^,\n]+)/i)
      // Yandex/корп format: "Заказчик: ИП ЕлемесовЛаб ... БИН: 880520351529"
      || text.match(/(?:Заказчик|Плательщик)[:\s]+((?:ИП|ТОО|АО|ОсОО|ООО)[^\n]+?)[\s\S]{0,200}?БИН[:\s]*(\d{12})/i)
      || text.match(/(?:Заказчик|Плательщик)[:\s]+([^\n]+)/i);
    if (buyerMatch) {
      if (buyerMatch[2]) {
        // 2 группы: для стандартного формата [1]=БИН [2]=имя; для Yandex [1]=имя [2]=БИН.
        // Различаем по длине: 12 цифр подряд = БИН.
        const a = buyerMatch[1] || '';
        const b = buyerMatch[2] || '';
        if (/^\d{12}$/.test(a.trim())) {
          result.buyer.bin = a.trim();
          result.buyer.name = b.trim().replace(/^["«\s]|["»\s]$/g, '').replace(/^\/+\s*,?\s*/, '');
        } else if (/^\d{12}$/.test(b.trim())) {
          result.buyer.name = a.trim().replace(/^["«\s]|["»\s]$/g, '').replace(/^\/+\s*,?\s*/, '');
          result.buyer.bin = b.trim();
        } else {
          // fallback к старому поведению
          result.buyer.bin = a.trim();
          result.buyer.name = b.trim().replace(/^["«\s]|["»\s]$/g, '').replace(/^\/+\s*,?\s*/, '');
        }
      } else {
        result.buyer.name = buyerMatch[1].trim().replace(/^["«]|["»]$/g, '');
      }
    }

    // === Бенефициар (поставщик) — IBAN ===
    // Ищем IBAN рядом с "Бенефициар" или "ИИК" — в любом порядке
    const benIbanMatch = text.match(/Бенефициар[^]*?(KZ[0-9A-Za-z]{18,23})/is)
      || text.match(/ИИК\s+(KZ[0-9A-Za-z]{18,23})/i);
    if (benIbanMatch) result.supplier.iban = cleanIBAN(benIbanMatch[1]);
    // Или первый найденный IBAN
    if (!result.supplier.iban && ibans.length > 0) result.supplier.iban = ibans[0];

    // === БИН бенефициара (если не нашли из Поставщик) ===
    const benBinMatch = text.match(/Бенефициар[^]*?(?:БИН\s*\/?\s*ИИН|ИИН\s*\/?\s*БИН)[:\s]*(\d{12})/is)
      || text.match(/БИН\s*\/?\s*ИИН[:\s]*(\d{12})/i);
    if (!result.supplier.bin && benBinMatch) result.supplier.bin = benBinMatch[1];

    // === Имя бенефициара (если Поставщик не дал имя) ===
    if (!result.supplier.name) {
      const benNameMatch = text.match(/Бенефициар[:\s]+([^К\n]+?)(?=\s*KZ|\s*$)/i);
      if (benNameMatch) result.supplier.name = benNameMatch[1].trim().replace(/^["«]|["»]$/g, '');
    }
    // (2026-06-09) Если «Поставщик» дал мусорное имя «БИН 0808…» (колоночная разбивка PDF) —
    // берём чистое «ТОО "…"» из блока Бенефициар.
    if (!result.supplier.name || /^(?:БИН|ИИН)[\s:]/i.test(result.supplier.name)) {
      const benClean = text.match(/Бенефициар[^]*?((?:ТОО|ИП|АО|ОсОО|ООО|ЗАО)\s*["«][^"«»\n]+["»])/i);
      if (benClean) result.supplier.name = benClean[1].replace(/\s+/g, ' ').trim();
    }

  } else if (result.docType === 'payment_order') {
    // Платежное поручение: Отправитель и Бенефициар
    const senderMatch = text.match(/Отправитель денег[:\s]+([^\n]+?)ИИН\s*\(БИН\)\s*[:\s]*(\d{12})/i);
    if (senderMatch) {
      result.buyer.name = senderMatch[1].trim();
      result.buyer.bin = senderMatch[2];
    }
    const benMatch = text.match(/Бенефициар[:\s]+([^\n]+?)ИИН\s*\(БИН\)\s*[:\s]*(\d{12})/i);
    if (benMatch) {
      result.supplier.name = benMatch[1].trim();
      result.supplier.bin = benMatch[2];
    }

    // IBAN отправителя и бенефициара
    const senderIban = text.match(/Отправитель[^]*?ИИК\s+(KZ[0-9A-Za-z]+)/is);
    const benIban = text.match(/Бенефициар[^]*?ИИК\s+(KZ[0-9A-Za-z]+)/is);
    if (senderIban) result.buyer.iban = cleanIBAN(senderIban[1]);
    if (benIban) result.supplier.iban = cleanIBAN(benIban[1]);

    // Описание платежа
    const descMatch = text.match(/(?:За\s+.+?услуги[^.]*\.)/i);
    if (descMatch) result.description = descMatch[0].trim();
  }

  // Фоллбэк: если не нашли — распределяем IBAN по порядку
  if (!result.supplier.iban && !result.buyer.iban && ibans.length >= 2) {
    result.supplier.iban = ibans[0];
    result.buyer.iban = ibans[1];
  }
  // Фоллбэк BIN
  if (!result.supplier.bin && !result.buyer.bin && bins.length >= 2) {
    result.supplier.bin = bins[0];
    result.buyer.bin = bins[1];
  }

  // (2026-06-09) Чистим мусорные имена сторон (порядок текста pdfjs).
  if (looksJunkName(result.supplier.name)) {
    const n = findNameNearBin(text, result.supplier.bin);
    if (n) result.supplier.name = n;
  }
  if (looksJunkName(result.buyer.name)) {
    const n = findNameNearBin(text, result.buyer.bin);
    if (n) result.buyer.name = n;
  }

  return result;
}

// Главная функция: парсит PDF и возвращает структурированные данные
export async function parseInvoice(file) {
  const text = await extractText(file);
  console.log('[INVOICE] Extracted text:', text.substring(0, 500));
  const ibans = findIBANs(text);
  console.log('[INVOICE] Found IBANs:', ibans);
  const bins = findBINs(text);
  console.log('[INVOICE] Found BINs:', bins);
  const amount = findAmount(text);
  console.log('[INVOICE] Found amount:', amount);
  const parties = parseParties(text, ibans, bins);
  console.log('[INVOICE] Parties:', JSON.stringify(parties, null, 2));

  return {
    rawText: text,
    ibans,
    bins,
    amount,
    ...parties,
  };
}

// Сопоставить данные инвойса с аккаунтами организации
export function matchInvoiceToOrg(parsed, accounts, contractors) {
  const result = {
    type: null,              // 'receivable' | 'payable'
    matchedAccountId: '',    // ID нашего счёта
    matchedContractorId: '', // ID контрагента (если нашли)
    counterparty: null,      // Данные контрагента из инвойса
    needNewContractor: false,
    typeUncertain: false,    // (2026-06-09) true если type — слепой fallback (не по IBAN/BIN)
  };

  // Ищем совпадение IBAN с нашими счетами
  const allIbans = [parsed.supplier.iban, parsed.buyer.iban].filter(Boolean);

  for (const acc of accounts.filter(a => !a.archived && a.iban)) {
    const accIban = cleanIBAN(acc.iban);

    if (parsed.supplier.iban && accIban === cleanIBAN(parsed.supplier.iban)) {
      // Наш счёт = поставщик/бенефициар → нам платят (receivable)
      result.type = 'receivable';
      result.matchedAccountId = acc.id;
      result.counterparty = parsed.buyer;
      break;
    }
    if (parsed.buyer.iban && accIban === cleanIBAN(parsed.buyer.iban)) {
      // Наш счёт = покупатель/отправитель → мы должны (payable)
      result.type = 'payable';
      result.matchedAccountId = acc.id;
      result.counterparty = parsed.supplier;
      break;
    }
  }

  // Если не определили по IBAN — пробуем по БИН
  if (!result.type) {
    for (const con of contractors) {
      const conBin = String(con.bin || '').trim();
      if (conBin && parsed.supplier.bin === conBin) {
        result.type = 'payable';
        result.counterparty = parsed.supplier;
        result.matchedContractorId = con.id;
        break;
      }
      if (conBin && parsed.buyer.bin === conBin) {
        result.type = 'receivable';
        result.counterparty = parsed.buyer;
        result.matchedContractorId = con.id;
        break;
      }
    }
  }

  // Fallback (Stage 11a): если ни IBAN, ни BIN не дали match — для счёта на оплату
  // по умолчанию это "мы должны" (нам выставили счёт от нового поставщика).
  // Покупатель в счёте часто пустой/обезличенный (БИН 000000000000) — игнорируем,
  // а поставщик заполнен и становится новым контрагентом.
  // Для платёжных поручений fallback не делаем — там Отправитель и Получатель оба
  // обычно заполнены, и если не определилось значит реально неопределимо.
  if (!result.type && parsed.docType === 'invoice' && parsed.supplier && (parsed.supplier.name || parsed.supplier.bin)) {
    result.type = 'payable';
    result.counterparty = parsed.supplier;
    result.typeUncertain = true; // догадка: ни IBAN наших счетов, ни БИН не дали match
  }

  // Ищем контрагента по БИН, затем (fallback) по имени.
  // Fix (2026-06-09): у существующего контрагента БИН мог быть null (создан раньше без БИН) →
  // match по имени находит его, иначе импорт каждый раз пишет «не найден» и плодит дубли.
  if (result.counterparty && !result.matchedContractorId) {
    const cpBin = String(result.counterparty.bin || '').trim();
    const cpName = String(result.counterparty.name || '').trim().toLowerCase();
    let found = null;
    if (cpBin) found = contractors.find(c => String(c.bin || '').trim() === cpBin);
    if (!found && cpName) found = contractors.find(c => String(c.name || '').trim().toLowerCase() === cpName);
    if (found) {
      result.matchedContractorId = found.id;
    } else if (cpBin || cpName) {
      result.needNewContractor = true;
    }
  }

  return result;
}

// Конвертировать File в base64 для хранения в localStorage
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
